import { useState } from 'react';
import { useApp, ROLES } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Download,
  FileText,
  FileArchive,
  Mail,
  MessageSquare,
  Search,
  CheckCircle,
  AlertTriangle,
  MapPin,
  Clock,
  User,
  Printer,
  Smartphone,
  Eye,
  Building2,
  Calendar,
  Layers,
  ChevronRight,
} from 'lucide-react';
import { fetchCases } from '../../api';
import { generatePrintableCaseReport } from '../../utils/printReport';

export default function DownloadReports() {
  const { currentRole, currentUserId } = useApp();
  const [liveCases, setLiveCases] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      try {
        const data = await fetchCases();
        if (isMounted && Array.isArray(data)) setLiveCases(data);
      } catch (err) {
        if (isMounted) setLiveCases([]);
      }
    }
    load();
    return () => { isMounted = false; };
  }, []);
  
  // Scope cases strictly to downline hierarchy
  const scopedCases = liveCases.filter(
    c => ['completed', 'report_sent', 'qc_approved', 'verified', 'qc_pending'].includes(c.status)
  );

  const [selectedCaseId, setSelectedCaseId] = useState(scopedCases[0]?.id || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('preview'); // preview, print, mobile
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionType, setActionType] = useState('');

  const selectedCase = scopedCases.find(c => c.id === selectedCaseId);

  const handleAction = (type) => {
    if (!selectedCase) {
      toast.error('Please select a case first');
      return;
    }
    setActionType(type);

    if (type === 'pdf' || type === 'print') {
      generatePrintableCaseReport(selectedCase);
      toast.success(`PDF report generated for ${selectedCase.applicantName}!`);
    } else if (type === 'zip') {
      const blob = new Blob([
        `MobileForce Watermarked Verification Package\nCase: ${selectedCase.refNumber}\nApplicant: ${selectedCase.applicantName}\nAddress: ${selectedCase.address}\nAudit Status: VERIFIED`
      ], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedCase.refNumber}_Watermarked_Photos.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`ZIP dossier for ${selectedCase.refNumber} downloaded successfully!`);
    } else if (type === 'email') {
      const origin = window.location.origin;
      const url = `${origin}/cases/${selectedCase.id}`;
      window.location.href = `mailto:${selectedCase.applicantEmail || ''}?subject=${encodeURIComponent(`Verification Dossier - ${selectedCase.refNumber}`)}&body=${encodeURIComponent(`Verification Report online: ${url}`)}`;
      toast.success(`Email client opened with verification dossier!`);
    } else if (type === 'whatsapp') {
      const origin = window.location.origin;
      const url = `${origin}/cases/${selectedCase.id}`;
      const msg = `🏢 *AD VENTURES - VERIFICATION REPORT*\nRef: ${selectedCase.refNumber}\nApplicant: ${selectedCase.applicantName}\nStatus: VERIFIED\nLink: ${url}`;
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`, '_blank');
      toast.success(`WhatsApp notification opened!`);
    }
  };

  const filteredCases = scopedCases.filter(c =>
    c.applicantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.refNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.client?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in text-slate-800" data-testid="download-reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">MobileForce Reports Download Center</h1>
          <p className="text-sm text-slate-500 mt-1">Review, print, and transmit downline verification dossiers and watermarked GPS catalogs.</p>
        </div>
      </div>

      {scopedCases.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-800">No Verified Cases</h3>
          <p className="text-sm text-slate-500 mt-1">There are no completed or verified cases under your hierarchy yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left Panel: Cases List */}
          <div className="lg:col-span-4 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm flex flex-col h-[750px]">
            <div className="p-4 border-b border-slate-100 bg-slate-50/50">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Search Cases</Label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Ref, applicant name, client..."
                  className="pl-9 bg-white border-slate-200 text-sm h-9"
                />
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="divide-y divide-slate-100">
                {filteredCases.map((c) => {
                  const isSelected = c.id === selectedCaseId;
                  const isPositive = c.decisionStatus === 'positive';
                  return (
                    <button
                      key={c.id}
                      onClick={() => setSelectedCaseId(c.id)}
                      className={`w-full text-left p-4 transition-colors hover:bg-slate-50 flex items-center justify-between ${
                        isSelected ? 'bg-blue-50/70 hover:bg-blue-50' : ''
                      }`}
                    >
                      <div className="space-y-1.5 min-w-0 pr-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">{c.refNumber}</span>
                          <Badge className={isPositive ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-emerald-100' : 'bg-red-50 text-red-700 hover:bg-red-50 border-red-100'}>
                            {isPositive ? 'Positive' : 'Negative'}
                          </Badge>
                        </div>
                        <h4 className="font-semibold text-sm text-slate-800 truncate">{c.applicantName}</h4>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5 shrink-0" />
                            {c.client?.name || 'HDFC Bank'}
                          </span>
                          <span className="flex items-center gap-1 truncate">
                            <Layers className="w-3.5 h-3.5 shrink-0" />
                            {c.verificationType}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isSelected ? 'translate-x-1 text-blue-500' : ''}`} />
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* Right Panel: Workspace */}
          <div className="lg:col-span-8 flex flex-col space-y-6">
            {selectedCase ? (
              <>
                {/* Document Controls */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                  {/* Mode Selector */}
                  <div className="flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200/50">
                    <button
                      onClick={() => setViewMode('preview')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                        viewMode === 'preview' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Preview
                    </button>
                    <button
                      onClick={() => setViewMode('print')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                        viewMode === 'print' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      <Printer className="w-3.5 h-3.5" />
                      Print View
                    </button>
                    <button
                      onClick={() => setViewMode('mobile')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                        viewMode === 'mobile' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      <Smartphone className="w-3.5 h-3.5" />
                      Mobile View
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 justify-end w-full sm:w-auto">
                    <Button onClick={() => handleAction('pdf')} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5 text-xs font-semibold">
                      <FileText className="w-3.5 h-3.5" />
                      Download PDF
                    </Button>
                    <Button onClick={() => handleAction('zip')} size="sm" variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 bg-white gap-1.5 text-xs font-semibold">
                      <FileArchive className="w-3.5 h-3.5" />
                      Watermarked ZIP
                    </Button>
                    <Button onClick={() => handleAction('email')} size="sm" variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 bg-white gap-1.5 text-xs font-semibold">
                      <Mail className="w-3.5 h-3.5" />
                      Email Report
                    </Button>
                    <Button onClick={() => handleAction('whatsapp')} size="sm" variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 bg-white gap-1.5 text-xs font-semibold">
                      <MessageSquare className="w-3.5 h-3.5" />
                      WhatsApp Notification
                    </Button>
                  </div>
                </div>

                {/* Report Canvas Container */}
                <div className={`flex justify-center transition-all ${viewMode === 'mobile' ? 'max-w-md mx-auto w-full' : 'w-full'}`}>
                  <div
                    className={`bg-white border border-slate-200 shadow-sm w-full overflow-hidden transition-all ${
                      viewMode === 'mobile'
                        ? 'rounded-[32px] border-8 border-slate-800 h-[750px] overflow-y-auto px-4 py-6 font-sans'
                        : 'rounded-xl p-8 max-w-4xl'
                    }`}
                  >
                    {/* SECTION 1: HEADER & LOGO */}
                    <div className="border-b-2 border-slate-200 pb-4 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                          MF
                        </div>
                        <div>
                          <h2 className="text-base font-extrabold text-slate-900 tracking-tight uppercase">MobileForce Verification Dossier</h2>
                          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">AD Ventures Network Services</p>
                        </div>
                      </div>
                      <div className="text-left sm:text-right shrink-0">
                        <div className="text-[11px] font-extrabold text-slate-400 uppercase">Case Dossier Ref</div>
                        <div className="text-sm font-bold text-blue-600">{selectedCase.refNumber}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5 font-medium">Mapped Client: {selectedCase.client?.name || 'HDFC Bank'}</div>
                      </div>
                    </div>

                    {/* SECTION 2: APPLICANT DETAILS */}
                    <div className="mb-6 space-y-3">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 pb-1 flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        Section 1: Applicant Profile
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200/50">
                        <div className="space-y-1">
                          <span className="text-[10px] uppercase font-bold text-slate-400">Applicant Full Name</span>
                          <p className="text-xs font-semibold text-slate-800">{selectedCase.applicantName}</p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-[10px] uppercase font-bold text-slate-400">Mobile Phone</span>
                          <p className="text-xs font-semibold text-slate-800">{selectedCase.applicantPhone || '+91 99887 76655'}</p>
                        </div>
                        <div className="sm:col-span-2 space-y-1">
                          <span className="text-[10px] uppercase font-bold text-slate-400">Verification Site Address</span>
                          <p className="text-xs font-medium text-slate-700">{selectedCase.address}</p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-[10px] uppercase font-bold text-slate-400">City / State</span>
                          <p className="text-xs font-semibold text-slate-800">{selectedCase.city || 'Ahmedabad'}, {selectedCase.state || 'Gujarat'}</p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-[10px] uppercase font-bold text-slate-400">Postal Pincode</span>
                          <p className="text-xs font-semibold text-slate-800">{selectedCase.pincode || '380007'}</p>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 3: DECISION STATUS BADGE */}
                    <div className="mb-6 space-y-3">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 pb-1 flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5 text-slate-400" />
                        Section 2: Verification Evaluation Verdict
                      </h3>
                      {selectedCase.decisionStatus === 'positive' ? (
                        <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800">
                          <CheckCircle className="w-8 h-8 text-emerald-600 shrink-0" />
                          <div>
                            <h4 className="text-sm font-bold text-emerald-950 uppercase tracking-wide">CLEAR VERIFICATION - POSITIVE FINDING</h4>
                            <p className="text-xs text-emerald-700 mt-0.5">Physical residence verification completed successfully. Identity matches standard client parameters.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                          <AlertTriangle className="w-8 h-8 text-red-600 shrink-0" />
                          <div>
                            <h4 className="text-sm font-bold text-red-950 uppercase tracking-wide">DECLINED / NEGATIVE VERIFICATION VERDICT</h4>
                            <p className="text-xs text-red-700 mt-0.5">Discrepancy triggered or premises inaccessible. Refer to remarks logs below.</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* SECTION 4: Q&A DYNAMIC GRIDS */}
                    <div className="mb-6 space-y-3">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 pb-1 flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5 text-slate-400" />
                        Section 3: Response Question Matrix
                      </h3>
                      <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <table className="w-full text-left text-xs border-collapse">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 font-semibold text-slate-600">
                              <th className="p-3 w-1/2">Field / Question Parameter</th>
                              <th className="p-3 w-1/2">Recorded Response</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {selectedCase.answers?.map((ans, idx) => (
                              <tr key={idx} className="hover:bg-slate-50/50">
                                <td className="p-3 font-semibold text-slate-600">{ans.question}</td>
                                <td className="p-3 font-medium text-slate-800">{ans.answer}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* SECTION 5: WATERMARKED GALLERY */}
                    <div className="mb-6 space-y-3">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 pb-1 flex items-center gap-1.5">
                        <Smartphone className="w-3.5 h-3.5 text-slate-400" />
                        Section 4: Watermarked Evidence Logs
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedCase.verifiedImages?.map((img, idx) => (
                          <div key={idx} className="relative rounded-lg overflow-hidden border border-slate-200 shadow-sm bg-slate-900 group aspect-video">
                            <img
                              src={img.url}
                              alt={img.label}
                              className="w-full h-full object-cover opacity-85"
                            />
                            {/* Watermark GPS Hud overlays */}
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent flex flex-col justify-end p-3 text-white text-[9px] font-mono leading-relaxed select-none">
                              <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-blue-600 text-[8px] uppercase tracking-wider font-extrabold text-white font-sans">
                                GPS STAMPED
                              </div>
                              <span className="font-sans font-bold text-[10px] text-yellow-400 truncate block mb-1">{img.label}</span>
                              <div className="flex items-center gap-1.5">
                                <MapPin className="w-3.5 h-3.5 text-red-500 shrink-0" />
                                <span>Lat/Long: {img.latitude.toFixed(6)}° N, {img.longitude.toFixed(6)}° E</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                                <span>Timestamp: {img.timestamp}</span>
                              </div>
                              <div className="truncate block opacity-90 mt-0.5 pl-5">Address: {img.address}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* SECTION 6: MAP GEOLOCATION HUD */}
                    <div className="mb-6 space-y-3">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 pb-1 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-slate-400" />
                        Section 5: Coordinate Geolocation locator HUD
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 border border-slate-200 rounded-lg overflow-hidden">
                        {/* Mock Map graphics HUD */}
                        <div className="md:col-span-2 bg-slate-100 relative min-h-[160px] flex items-center justify-center overflow-hidden">
                          {/* Mini visual mock map representation using canvas placeholders */}
                          <div className="absolute inset-0 opacity-70 flex flex-col justify-between p-2">
                            <div className="flex justify-between w-full text-[9px] font-mono text-slate-400">
                              <span>Grid scale: 100m</span>
                              <span>GPS Lock: 3D High Accuracy</span>
                            </div>
                            <div className="flex justify-between w-full text-[9px] font-mono text-slate-400">
                              <span>UTM Zone: 43Q</span>
                              <span>Datum: WGS84</span>
                            </div>
                          </div>
                          
                          {/* Map Pin Target */}
                          <div className="relative z-10 flex flex-col items-center gap-1">
                            <div className="w-7 h-7 rounded-full bg-blue-100 border border-blue-500 flex items-center justify-center animate-ping absolute" />
                            <div className="w-8 h-8 rounded-full bg-blue-600 border border-white flex items-center justify-center text-white shadow-md relative z-20">
                              <MapPin className="w-4 h-4" />
                            </div>
                            <span className="text-[9px] bg-slate-900 text-white font-semibold font-mono rounded px-1 relative z-20">
                              TARGET LOCKED
                            </span>
                          </div>

                          {/* Grid Overlay Lines */}
                          <div className="absolute inset-0 bg-[linear-gradient(to_right,#cbd5e1_1px,transparent_1px),linear-gradient(to_bottom,#cbd5e1_1px,transparent_1px)] bg-[size:20px_20px] opacity-25" />
                        </div>

                        {/* Location Details HUD Column */}
                        <div className="p-4 bg-slate-50 border-t md:border-t-0 md:border-l border-slate-200 space-y-3 font-mono text-[10px] text-slate-600">
                          <h4 className="font-sans font-bold text-xs uppercase tracking-wide text-slate-800">Telemetry Specs</h4>
                          
                          <div className="space-y-2">
                            <div>
                              <span className="text-slate-400 block uppercase text-[8px] font-sans">Target Latitude</span>
                              <span className="font-semibold text-slate-800 text-[11px]">{(selectedCase.mapCoords?.latitude || 23.0225).toFixed(6)}° N</span>
                            </div>
                            <div>
                              <span className="text-slate-400 block uppercase text-[8px] font-sans">Target Longitude</span>
                              <span className="font-semibold text-slate-800 text-[11px]">{(selectedCase.mapCoords?.longitude || 72.5714).toFixed(6)}° E</span>
                            </div>
                            <div>
                              <span className="text-slate-400 block uppercase text-[8px] font-sans">Accuracy Range</span>
                              <span className="font-semibold text-emerald-600">± 2.8 Meters (3D Locked)</span>
                            </div>
                            <div>
                              <span className="text-slate-400 block uppercase text-[8px] font-sans">Positioning Mode</span>
                              <span className="font-semibold text-slate-800">Assisted GPS + GLONASS</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 7: REMARKS LOGS */}
                    <div className="space-y-3">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 pb-1 flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5 text-slate-400" />
                        Section 6: Verification Remarks & Sign-offs
                      </h3>
                      
                      <div className="space-y-3">
                        {/* FE Remarks */}
                        <div className="p-4 bg-slate-50/60 rounded-lg border border-slate-200/50 space-y-1.5">
                          <div className="flex items-center justify-between text-[10px] uppercase font-bold text-slate-500">
                            <span>Field Executive Findings</span>
                            <span>{selectedCase.assignedTo?.name || 'Vikram Singh'} (FE)</span>
                          </div>
                          <p className="text-xs italic text-slate-700 leading-relaxed">
                            "{selectedCase.remarks?.feRemark || 'Resides at the address. Clear verification recommended.'}"
                          </p>
                        </div>

                        {/* QC Remarks */}
                        <div className="p-4 bg-slate-50/60 rounded-lg border border-slate-200/50 space-y-1.5">
                          <div className="flex items-center justify-between text-[10px] uppercase font-bold text-slate-500">
                            <span>Quality Checking Audit</span>
                            <span>Sneha Reddy (QC Supervisor)</span>
                          </div>
                          <p className="text-xs italic text-slate-700 leading-relaxed">
                            "{selectedCase.remarks?.qcRemark || 'All checks conform to criteria. Verification photos are valid.'}"
                          </p>
                        </div>

                        {/* Final Remarks */}
                        <div className="p-4 bg-blue-50/50 rounded-lg border border-blue-100 space-y-1.5">
                          <div className="flex items-center justify-between text-[10px] uppercase font-bold text-blue-600">
                            <span>Supreme Manager Audit Sign-off</span>
                            <span>Priya Sharma (Supreme Manager)</span>
                          </div>
                          <p className="text-xs font-semibold text-slate-800 leading-relaxed">
                            {selectedCase.remarks?.finalRemark || 'Dossier successfully processed and approved.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center shadow-sm">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-800">Select a Case</h3>
                <p className="text-sm text-slate-500 mt-1">Pick a verified case from the downline roster to inspect or download the MobileForce dossier.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Simulator Overlay Loading screen */}
      {isProcessing && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full text-center border border-slate-200 space-y-4">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <div>
              <h4 className="font-semibold text-slate-800 text-sm">Processing Transmittal Request</h4>
              <p className="text-xs text-slate-500 mt-1">Compiling watermarked graphics, coordinates overlays, and Remarks logs...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
